import React, { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { PieChart as PieIcon } from "lucide-react";
import { TOOLTIP_CONTENT_STYLE } from "../lib/chartStyles";
import { fmtMoney, fmtPct } from "../lib/api";

// Reusable "Allocation" donut + right-side legend. Used by Holdings to show
// asset-type and sector breakdowns of the currently-filtered rows.
//
// Props:
//   title       — "By Asset Type" / "By Sector"
//   rows        — array of holdings (each must expose market_value + the
//                 grouping key). MUST be passed in already-filtered if you
//                 want client-side filter sync.
//   groupKey    — string, e.g. "asset_type" | "sector"
//   labelMap    — { [groupValue]: "Display label" } (optional)
//   colorMap    — { [groupValue]: "#hex" } (optional; falls back to palette)
//   emptyLabel  — bucket label for rows with null/empty groupKey
//   testId      — base data-testid prefix
//
// Slices < 1.5% of total are merged into "Other" so the donut stays
// readable on portfolios with a long tail (you can still see them in
// the legend).
const FALLBACK_PALETTE = [
  "#9e8193", "#c9b896", "#88a17e", "#bfaf9d", "#d7b5c9",
  "#a86464", "#5e6b88", "#dbd6d2", "#b89b7a", "#7a8f7a",
];

export default function AllocationDonut({
  title,
  rows,
  groupKey,
  labelMap = {},
  colorMap = {},
  emptyLabel = "Unclassified",
  testId = "allocation",
}) {
  const data = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    const totals = new Map();
    let grandTotal = 0;
    rows.forEach((r) => {
      const raw = r?.[groupKey];
      const key = raw == null || raw === "" ? "__empty" : String(raw);
      const mv = Number(r.market_value || 0);
      grandTotal += mv;
      const slot = totals.get(key) || { key, market_value: 0, positions: 0, annual_income: 0 };
      slot.market_value += mv;
      slot.positions += 1;
      slot.annual_income += Number(r.annual_income || 0);
      totals.set(key, slot);
    });
    if (grandTotal === 0) return [];
    // Merge tiny tail slices into "Other (n)" to keep the donut readable.
    const all = [...totals.values()].sort((a, b) => b.market_value - a.market_value);
    const big = [];
    const tail = [];
    all.forEach((s) => {
      const pct = (s.market_value / grandTotal) * 100;
      (pct >= 1.5 ? big : tail).push({ ...s, pct });
    });
    if (tail.length > 0) {
      const merged = tail.reduce(
        (acc, s) => ({
          key: "__tail",
          market_value: acc.market_value + s.market_value,
          positions: acc.positions + s.positions,
          annual_income: acc.annual_income + s.annual_income,
          name: `Other (${tail.length})`,
          tailMembers: [...(acc.tailMembers || []), s.key],
        }),
        { market_value: 0, positions: 0, annual_income: 0, tailMembers: [] },
      );
      merged.pct = (merged.market_value / grandTotal) * 100;
      big.push(merged);
    }
    let palettePtr = 0;
    return big.map((s) => {
      const displayKey = s.key;
      const name = s.name || (displayKey === "__empty" ? emptyLabel : (labelMap[displayKey] || displayKey));
      const color = colorMap[displayKey] || FALLBACK_PALETTE[palettePtr++ % FALLBACK_PALETTE.length];
      return { ...s, name, color };
    });
  }, [rows, groupKey, labelMap, colorMap, emptyLabel]);

  if (data.length === 0) {
    return (
      <div className="border border-stone bg-champagne p-6" data-testid={`${testId}-card`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold">// {title}</div>
          <PieIcon className="w-4 h-4 text-graphite/30" />
        </div>
        <div className="text-xs text-graphite/45 font-mono text-center py-10">No data</div>
      </div>
    );
  }

  return (
    <div className="border border-stone bg-champagne p-6" data-testid={`${testId}-card`}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold">// {title}</div>
        <PieIcon className="w-4 h-4 text-paars-dark" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
        <div className="h-52" data-testid={`${testId}-chart`}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="market_value"
                nameKey="name"
                innerRadius={45}
                outerRadius={80}
                paddingAngle={1}
                stroke="#f2f2f2"
                strokeWidth={2}
              >
                {data.map((s) => (
                  <Cell key={s.key} fill={s.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={TOOLTIP_CONTENT_STYLE}
                formatter={(v, _n, p) => [`${fmtMoney(v)} · ${p.payload.positions} pos`, p.payload.name]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-px bg-stone max-h-52 overflow-y-auto" data-testid={`${testId}-legend`}>
          {data.map((s) => (
            <div
              key={s.key}
              data-testid={`${testId}-row-${s.key.replace(/[^a-z0-9]/gi, "_")}`}
              className="bg-champagne flex items-center justify-between p-2.5"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-2.5 h-2.5 flex-shrink-0" style={{ background: s.color }} />
                <div className="min-w-0">
                  <div className="text-xs font-mono text-graphite truncate">{s.name}</div>
                  <div className="text-[10px] font-mono text-graphite/55">{s.positions} pos</div>
                </div>
              </div>
              <div className="text-right flex-shrink-0 ml-2">
                <div className="text-xs font-mono text-graphite">{fmtMoney(s.market_value)}</div>
                <div className="text-[10px] font-mono text-paars-dark">{fmtPct(s.pct)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
